/**
 * NeutralizationTimeSeriesChart — token-driven line chart for trajectories
 * over a discrete step axis (μ-descent in AttackSurfaceConvergence,
 * neutralization-count growth, governance-budget burndown, etc.).
 *
 * The chart accepts one or more named series so callers compare CSK
 * vs vulnerable trajectories on the same axis. Series colours flow
 * through the token-aligned palette by default; a series may pin a
 * specific theorem name to inherit its visual identity.
 *
 * Accessibility: the chart sets `role="img"` with an explicit
 * `aria-label`. The marker prop renders a highlighted dot at a
 * caller-controlled step, mirroring the canvas-based μ chart in the
 * Convergence Simulator panel.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useMemo } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  CHART_FONT,
  DEFAULT_CHART_PALETTE,
  type ChartPalette,
  theoremSeriesColor,
} from "./ChartTheme.js";

export interface TimeSeriesPoint {
  /** Discrete step index (1-D ordinal axis). */
  readonly step: number;
  /**
   * Value per series. Keys correspond to {@link TimeSeriesDefinition.key}.
   * Missing or `undefined` values render as gaps in the chart — the
   * type intentionally allows omission so callers can model partial
   * trajectories (a series that only appears mid-trace, a missing
   * sample point, etc.).
   */
  readonly [seriesKey: string]: number | undefined;
}

export interface TimeSeriesDefinition {
  /** Stable identifier matching the per-point data key. */
  readonly key: string;
  /** Human-readable label shown in the tooltip and legend. */
  readonly label: string;
  /**
   * Optional explicit stroke colour. When omitted the chart resolves a
   * theorem-keyed colour via {@link theoremSeriesColor}.
   */
  readonly color?: string;
  /** Optional canonical theorem name for token lookup. */
  readonly theorem?: string;
  /** Stroke style — defaults to a solid line. */
  readonly variant?: "solid" | "dashed";
}

export interface TimeSeriesMarker {
  /** Series key the marker sits on. */
  readonly seriesKey: string;
  /** Step index of the marker. */
  readonly step: number;
  /** Optional explicit value; resolved from data when omitted. */
  readonly value?: number;
  /** Marker radius in pixels — defaults to 5. */
  readonly radius?: number;
}

export interface NeutralizationTimeSeriesChartProps {
  /** Time-series rows; one per step. */
  readonly data: readonly TimeSeriesPoint[];
  /** Series definitions; renders one line per entry. */
  readonly series: readonly TimeSeriesDefinition[];
  /** Pixel height for the responsive container. Defaults to 220. */
  readonly height?: number;
  /** Y-axis label rendered along the value column. */
  readonly valueLabel?: string;
  /** X-axis label rendered below the step column. */
  readonly stepLabel?: string;
  /** Accessible label announced by assistive tech. */
  readonly ariaLabel: string;
  /** Highlight one or more points (e.g. current convergence step). */
  readonly markers?: readonly TimeSeriesMarker[];
  /** Y-axis scale. Defaults to linear. */
  readonly valueScale?: "linear" | "log";
  /** Optional fixed y-axis domain. Required by callers that need stable visual scale. */
  readonly valueDomain?: readonly [number, number];
  /** Format y-axis and tooltip values. */
  readonly valueFormatter?: (value: number) => string;
  /** Whether the y-axis may render decimal ticks. Defaults to false. */
  readonly allowDecimals?: boolean;
  /** Render the Recharts legend. Defaults to false to preserve compact existing charts. */
  readonly showLegend?: boolean;
  /** Palette override; falls back to {@link DEFAULT_CHART_PALETTE}. */
  readonly palette?: ChartPalette;
}

interface ResolvedTimeSeriesDefinition extends TimeSeriesDefinition {
  readonly stroke: string;
}

function resolveStroke(series: TimeSeriesDefinition, palette: ChartPalette): string {
  if (series.color !== undefined) return series.color;
  if (series.theorem !== undefined) return theoremSeriesColor(series.theorem, palette);
  return palette.primary;
}

const DEFAULT_RESPONSIVE_WIDTH = 640;

function buildStepIndex(data: readonly TimeSeriesPoint[]): ReadonlyMap<number, TimeSeriesPoint> {
  const byStep = new Map<number, TimeSeriesPoint>();
  for (const point of data) {
    byStep.set(point.step, point);
  }
  return byStep;
}

function resolveIndexedValue(
  dataByStep: ReadonlyMap<number, TimeSeriesPoint>,
  step: number,
  seriesKey: string,
): number | undefined {
  const raw = dataByStep.get(step)?.[seriesKey];
  return typeof raw === "number" ? raw : undefined;
}

export const NeutralizationTimeSeriesChart = React.memo(function NeutralizationTimeSeriesChart({
  data,
  series,
  height = 220,
  valueLabel,
  stepLabel,
  ariaLabel,
  markers,
  valueScale = "linear",
  valueDomain,
  valueFormatter,
  allowDecimals = false,
  showLegend = false,
  palette = DEFAULT_CHART_PALETTE,
}: NeutralizationTimeSeriesChartProps): React.JSX.Element {
  const resolvedSeriesIndex = useMemo(() => {
    const entries: ResolvedTimeSeriesDefinition[] = [];
    const strokeByKey = new Map<string, string>();
    for (const entry of series) {
      const stroke = resolveStroke(entry, palette);
      entries.push({ ...entry, stroke });
      strokeByKey.set(entry.key, stroke);
    }
    return { entries, strokeByKey };
  }, [series, palette]);
  const resolvedSeries = resolvedSeriesIndex.entries;
  const chartData = useMemo(() => [...data], [data]);
  const dataByStep = useMemo(() => buildStepIndex(data), [data]);

  const resolvedMarkers = useMemo(() => {
    if (markers === undefined) return [] as const;
    return markers.map((marker) => {
      const value = marker.value ?? resolveIndexedValue(dataByStep, marker.step, marker.seriesKey);
      return {
        ...marker,
        fill: resolvedSeriesIndex.strokeByKey.get(marker.seriesKey) ?? palette.primary,
        value,
      };
    });
  }, [markers, dataByStep, resolvedSeriesIndex, palette]);

  return (
    <div
      role="img"
      aria-label={ariaLabel}
      data-csk-chart="neutralization-time-series"
      style={{ width: "100%", height }}
    >
      <ResponsiveContainer
        width="100%"
        height="100%"
        minWidth={0}
        minHeight={height}
        initialDimension={{ width: DEFAULT_RESPONSIVE_WIDTH, height }}
      >
        <LineChart data={chartData} margin={{ top: 12, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid stroke={palette.grid} strokeDasharray="3 3" />
          <XAxis
            dataKey="step"
            stroke={palette.axisLine}
            tick={{ fill: palette.axis, fontSize: CHART_FONT.axisSize }}
            tickLine={false}
            {...(stepLabel !== undefined && {
              label: {
                value: stepLabel,
                position: "insideBottom",
                offset: -4,
                fill: palette.axis,
                fontSize: CHART_FONT.axisSize,
              },
            })}
          />
          <YAxis
            type="number"
            scale={valueScale}
            stroke={palette.axisLine}
            tick={{ fill: palette.axis, fontSize: CHART_FONT.axisSize }}
            tickLine={false}
            allowDecimals={allowDecimals}
            {...(valueDomain !== undefined && { domain: [...valueDomain] })}
            {...(valueFormatter !== undefined && { tickFormatter: valueFormatter })}
            {...(valueLabel !== undefined && {
              label: {
                value: valueLabel,
                angle: -90,
                position: "insideLeft",
                fill: palette.axis,
                fontSize: CHART_FONT.axisSize,
              },
            })}
          />
          <Tooltip
            cursor={{ stroke: palette.grid, strokeWidth: 1 }}
            {...(valueFormatter !== undefined && {
              formatter: (value: unknown): string => {
                if (typeof value === "number") return valueFormatter(value);
                if (typeof value === "string") return value;
                if (Array.isArray(value)) {
                  return value
                    .map((entry) => {
                      if (typeof entry === "number") return valueFormatter(entry);
                      if (typeof entry === "string") return entry;
                      return "";
                    })
                    .filter((entry) => entry.length > 0)
                    .join(", ");
                }
                return "";
              },
            })}
            contentStyle={{
              background: palette.tooltipBg,
              border: `1px solid ${palette.tooltipBorder}`,
              color: palette.tooltipText,
              fontSize: CHART_FONT.tooltipSize,
              borderRadius: 6,
            }}
          />
          {showLegend ? (
            <Legend
              verticalAlign="top"
              height={24}
              wrapperStyle={{
                color: palette.axis,
                fontSize: CHART_FONT.legendSize,
                lineHeight: "20px",
              }}
            />
          ) : null}
          {resolvedSeries.map((entry) => (
            <Line
              key={entry.key}
              type="monotone"
              dataKey={entry.key}
              name={entry.label}
              stroke={entry.stroke}
              strokeWidth={2}
              {...(entry.variant === "dashed" && { strokeDasharray: "5 5" })}
              dot={false}
              activeDot={{ r: 4 }}
              isAnimationActive={false}
            />
          ))}
          {resolvedMarkers.map(
            (marker) =>
              marker.value !== undefined && (
                <ReferenceDot
                  key={`${marker.seriesKey}-${marker.step}`}
                  x={marker.step}
                  y={marker.value}
                  r={marker.radius ?? 5}
                  fill={marker.fill}
                  stroke={palette.tooltipBg}
                  strokeWidth={2}
                />
              ),
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
});
